import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calendar, List, ShoppingCart, Menu, X, User as UserIcon, LogOut, Loader2, Newspaper, FileDown, HelpCircle, Mail, Cloud, MapPin, ExternalLink, ChevronDown, ClipboardList, CreditCard, Images } from "lucide-react";
import { useCheckoutStore } from "../../stores/checkout-store";
import { useUser } from "../../lib/api-client";
import { useCheckout } from "../../lib/basket-api";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "../../lib/firebase";

export const Header = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { checkoutId } = useCheckoutStore();
  const { data: user, isLoading: isUserLoading, isAuthenticated } = useUser();
  // useCheckout handles ownership errors automatically, so we can safely ignore errors here
  const { data: checkoutResponse } = useCheckout(checkoutId);
  const checkout = checkoutResponse?.checkout;
  const basketItems = checkout?.items || [];
  const hasItems = basketItems.length > 0;

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await auth.signOut();
    queryClient.invalidateQueries({ queryKey: ["get", "/api/user/me", {}] });
    queryClient.invalidateQueries({ queryKey: ["get", "/api/user", {}] });
    // Remove cached user data to prevent stale data from appearing
    queryClient.removeQueries({ queryKey: ["get", "/api/user/me", {}] });
    queryClient.removeQueries({ queryKey: ["get", "/api/user", {}] });
  };

  return (
    <header className="sticky top-0 bg-white border-b border-gray-200 z-[101]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
            <div className="w-8 h-8 bg-primary-600 rounded flex items-center justify-center mr-3">
              <svg
                className="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900 hidden sm:inline">
              North Lincolnshire and Humberside Sailing Club
            </span>
            <span className="text-xl font-bold text-gray-900 sm:hidden">
              NLHSC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {/* Events dropdown */}
            <div className="relative group">
              <button
                type="button"
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/events") || isActive("/events/calendar")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
                aria-expanded={false}
                aria-haspopup="true"
                aria-label="Events menu"
              >
                <List className="w-4 h-4" />
                <span>Events</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all z-10">
                <div className="bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[140px]">
                  <Link
                    to="/events"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/events") && !isActive("/events/calendar")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <List className="w-4 h-4" />
                    <span>Events</span>
                  </Link>
                  <Link
                    to="/events/calendar"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/events/calendar")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Calendar</span>
                  </Link>
                  <Link
                    to="/faqs"
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>FAQs</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Club dropdown */}
            <div className="relative group">
              <button
                type="button"
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/news") ||
                  isActive("/downloads") ||
                  isActive("/gallery") ||
                  isActive("/weather") ||
                  isActive("/find_us") ||
                  isActive("/contact_us") ||
                  isActive("/pay")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
                aria-expanded={false}
                aria-haspopup="true"
                aria-label="Club menu"
              >
                <Newspaper className="w-4 h-4" />
                <span>Club</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all z-10">
                <div className="bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[140px]">
                  <Link
                    to="/news"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/news")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <Newspaper className="w-4 h-4" />
                    <span>News</span>
                  </Link>
                  <Link
                    to="/downloads"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/downloads")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <FileDown className="w-4 h-4" />
                    <span>Downloads</span>
                  </Link>
                  <Link
                    to="/gallery"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/gallery")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <Images className="w-4 h-4" />
                    <span>Gallery</span>
                  </Link>
                  <Link
                    to="/faqs"
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>FAQs</span>
                  </Link>
                  <Link
                    to="/weather"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/weather")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <Cloud className="w-4 h-4" />
                    <span>Weather</span>
                  </Link>
                  <Link
                    to="/find_us"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/find_us")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <MapPin className="w-4 h-4" />
                    <span>Find us</span>
                  </Link>
                  <Link
                    to="/pay"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/pay")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Honesty pay</span>
                  </Link>
                  <Link
                    to="/contact_us"
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive("/contact_us")
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    <span>Contact</span>
                  </Link>
                </div>
              </div>
            </div>

            <a
              href="https://members.nlhsc.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Members</span>
            </a>
            <Link
              to="/basket"
              className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive("/basket")
                  ? "text-primary-600 bg-primary-50"
                  : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
              }`}
            >
              <div className="relative">
                <ShoppingCart className="w-4 h-4" />
                {hasItems && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full border-2 border-white"></span>
                )}
              </div>
              <span>Basket</span>
            </Link>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-gray-200">
                <Link
                  to="/bookings"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive("/bookings")
                      ? "text-primary-600 bg-primary-50"
                      : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                  }`}
                >
                  <ClipboardList className="w-4 h-4" />
                  <span>Bookings</span>
                </Link>
                <Link
                  to="/profile"
                  className={`flex items-center space-x-2 text-sm transition-colors ${
                    isActive("/profile")
                      ? "text-primary-600"
                      : "text-gray-700 hover:text-primary-600"
                  }`}
                >
                  {isUserLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  ) : (
                    <UserIcon className="w-4 h-4" />
                  )}
                  <span className="font-medium">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email || "Loading..."}
                  </span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ml-4 ${
                  isActive("/login")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600"
                }`}
              >
                <UserIcon className="w-4 h-4" />
                <span>Login</span>
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-2">
              <Link
                to="/events"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/events") && !isActive("/events/calendar")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <List className="w-4 h-4" />
                <span>Events</span>
              </Link>
              <Link
                to="/events/calendar"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/events/calendar")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <Calendar className="w-4 h-4" />
                <span>Calendar</span>
              </Link>
              <Link
                to="/faqs"
                onClick={closeMobileMenu}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-gray-700 hover:text-primary-600 hover:bg-gray-50"
              >
                <HelpCircle className="w-4 h-4" />
                <span>FAQs</span>
              </Link>
              <Link
                to="/news"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/news")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <Newspaper className="w-4 h-4" />
                <span>News</span>
              </Link>
              <Link
                to="/downloads"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/downloads")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <FileDown className="w-4 h-4" />
                <span>Downloads</span>
              </Link>
              <Link
                to="/gallery"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/gallery")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <Images className="w-4 h-4" />
                <span>Gallery</span>
              </Link>
              <Link
                to="/weather"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/weather")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <Cloud className="w-4 h-4" />
                <span>Weather</span>
              </Link>
              <Link
                to="/find_us"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/find_us")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <MapPin className="w-4 h-4" />
                <span>Find us</span>
              </Link>
              <Link
                to="/pay"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/pay")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Honesty pay</span>
              </Link>
              <Link
                to="/contact_us"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/contact_us")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <Mail className="w-4 h-4" />
                <span>Contact</span>
              </Link>
              <a
                href="https://members.nlhsc.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Members</span>
              </a>
              <Link
                to="/basket"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive("/basket")
                    ? "text-primary-600 bg-primary-50"
                    : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                }`}
              >
                <div className="relative">
                  <ShoppingCart className="w-4 h-4" />
                  {hasItems && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full border-2 border-white"></span>
                  )}
                </div>
                <span>Basket</span>
              </Link>
              
              {isAuthenticated ? (
                <>
                  <div className="pt-2 pb-1 px-3 border-t border-gray-100 mt-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">User</p>
                    <Link
                      to="/bookings"
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-2 mt-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive("/bookings")
                          ? "text-primary-600 bg-primary-50"
                          : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                      }`}
                    >
                      <ClipboardList className="w-4 h-4" />
                      <span>Bookings</span>
                    </Link>
                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-2 mt-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive("/profile")
                          ? "text-primary-600 bg-primary-50"
                          : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                      }`}
                    >
                      {isUserLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      ) : (
                        <UserIcon className="w-4 h-4" />
                      )}
                      <span className="font-medium">
                        {user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.email || "Loading..."}
                      </span>
                    </Link>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive("/login")
                      ? "text-primary-600 bg-primary-50"
                      : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                  }`}
                >
                  <UserIcon className="w-4 h-4" />
                  <span>Login</span>
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
